const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const foodTypes = new Set(['food', 'veg', 'non-veg', 'non veg', 'nonveg', 'vegetarian', 'non-vegetarian']);
const barTypes = new Set([
  'beer', 'wine', 'lab', 'liquor', 'spirit', 'spirits', 'whisky', 'whiskey',
  'rum', 'vodka', 'gin', 'brandy', 'tequila', 'scotch', 'breezer', 'brezeer',
  'cocktail', 'cocktails', 'mocktail', 'mocktails', 'non alcoholic', 'non alchoholic',
]);

function metadataGroup(item, includeOtherCategory = true) {
  const source = normalize(item?.menuSource || item?.source);
  if (source === 'food') return 'food';
  if (['wine', 'bar', 'beer'].includes(source)) return 'bar';
  const category = normalize(item?.category);
  if (category === 'beer' || category.startsWith('beer ')) return 'bar';
  if (barTypes.has(category)) return 'bar';
  if (item?.stockMl !== undefined && item?.stockMl !== null && item.stockMl !== '') return 'bar';
  if (barTypes.has(normalize(item?.itemType))) return 'bar';
  if (foodTypes.has(normalize(item?.type)) || foodTypes.has(normalize(item?.itemType))) return 'food';
  // A registered non-bar category is more reliable than a word in a dish name.
  if (includeOtherCategory && category) return 'food';
  return null;
}

function catalogGroup(item, catalogItems) {
  const name = normalize(item?.name);
  if (!name) return null;
  const baseName = name.replace(/\s+(?:bottle|(?:30|60)\s*ml(?:\s+peg)?|half\s+plate)$/, '');
  for (const candidate of [...new Set([name, baseName])]) {
    const matches = catalogItems.filter((entry) => normalize(entry?.name) === candidate);
    if (!matches.length) continue;
    const groups = [...new Set(matches.map((entry) => metadataGroup(entry)).filter(Boolean))];
    // A legacy name alone cannot distinguish two different menu departments.
    // Refuse a wrong kitchen/bar slip instead of silently choosing one.
    if (groups.length > 1) {
      const error = new Error('Same item name exists in Food and Bar. Check its category before reprinting.');
      error.code = 'KOT_CATEGORY_AMBIGUOUS';
      throw error;
    }
    return groups[0] || null;
  }
  return null;
}

export function splitKotItems(items, catalogItems = []) {
  const groups = { foodItems: [], barItems: [] };
  const catalog = Array.isArray(catalogItems) ? catalogItems : [];
  for (const item of Array.isArray(items) ? items : []) {
    const group = metadataGroup(item, false) || catalogGroup(item, catalog)
      || (normalize(item?.category) ? 'food' : null);
    // Keep the old bottle/peg fallback only for legacy rows without metadata.
    // The word "beer" by itself is never enough to classify a dish as a bar item.
    const isBar = group ? group === 'bar' : /bottle|(?:30|60)\s*ml/.test(normalize(item?.name));
    groups[isBar ? 'barItems' : 'foodItems'].push(item);
  }
  return groups;
}

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character]));

export function buildKotPrintDocument({
  title, items = [], tableNo, kotNo, serverName, date, time, quantityMode = 'quantity',
  showPrices = false, totalBilling,
}) {
  const rows = (Array.isArray(items) ? items : []).map((item) => {
    const quantity = quantityMode === 'delta' ? item.newQuantity - item.oldQuantity : item.quantity;
    return `<tr><td class="name">${escapeHtml(item.name)}${item.note ? ` (${escapeHtml(item.note)})` : ''}</td><td class="quantity">${escapeHtml(quantity)}</td>${showPrices ? `<td class="price">${escapeHtml(item.price)}</td>` : ''}</tr>`;
  }).join('');
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  /* Use the printer's 80mm roll profile; keep text inside its printable area. */
  @page { size: auto; margin: 0; }
  body { width: 80mm; max-width: 100%; box-sizing: border-box; margin: 0; padding: 0 4mm; font-family: sans-serif; font-size: 12px; overflow-wrap: anywhere; word-break: normal; }
  h2 { font-size: 14px; margin-bottom: 8px; }
  p { margin: 3px 0; font-size: 12px; }
  table { width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 12px; }
  th, td { box-sizing: border-box; padding: 4px; border: 1px solid #000; overflow-wrap: anywhere; }
  .name { width: ${showPrices ? '60%' : '75%'}; text-align: left; }
  .quantity { width: ${showPrices ? '20%' : '25%'}; text-align: center; }
  .price { width: 20%; text-align: right; }
</style></head><body>
<h2>${escapeHtml(title)}</h2>
<p>Table No.: ${escapeHtml(tableNo)}</p>
<p>KOT No.: ${escapeHtml(kotNo)}</p>
<p>Server: ${escapeHtml(serverName)}</p>
<p>Date: ${escapeHtml(date)}</p>
<p>Time: ${escapeHtml(time)}</p>
${totalBilling !== undefined && totalBilling !== null ? `<p>Order total: ₹${escapeHtml(totalBilling)}</p>` : ''}
<table><thead><tr><th class="name">Name</th><th class="quantity">Quantity</th>${showPrices ? '<th class="price">Price</th>' : ''}</tr></thead><tbody>${rows}</tbody></table>
</body></html>`;
}

function printDocument(documentHtml) {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.title = 'KOT print document';
    iframe.style.position = 'fixed';
    iframe.style.top = '-9999px';
    document.body.appendChild(iframe);
    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      iframe.remove();
      reject(new Error('Could not open the KOT print document.'));
      return;
    }
    let settled = false;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      printWindow.removeEventListener('afterprint', afterPrint);
      iframe.remove();
      if (error) reject(error); else resolve();
    };
    const afterPrint = () => finish();
    try {
      const printDocument = printWindow.document;
      printDocument.open();
      printDocument.write(documentHtml);
      printDocument.close();
      printWindow.addEventListener('afterprint', afterPrint);
      // Allow the newly written document to finish layout before opening print.
      // Cleanup deliberately waits for afterprint, not an arbitrary timer: some
      // browsers return from print() while the print dialog is still open.
      setTimeout(() => {
        if (settled) return;
        try {
          printWindow.focus();
          printWindow.print();
        } catch (error) { finish(error); }
      }, 0);
    } catch (error) { finish(error); }
  });
}

let printQueue = Promise.resolve();

export function printKotDocuments(documents) {
  const next = printQueue.catch(() => {}).then(async () => {
    for (const documentHtml of Array.isArray(documents) ? documents : []) {
      if (documentHtml) await printDocument(documentHtml);
    }
  });
  printQueue = next;
  return next;
}
